import { useEffect } from "react";
import Head from "next/head";
import Image from "next/image";

import Typography from "@mui/material/Typography";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import MainHeader from "../components/MainHeader";
import MainFooter from "../components/MainFooter";
import DemoForm from "../components/forms/Form";
import YoutubeEmbed from "../components/YoutubeEmbed";

import { getCookie } from "cookies-next";

import fonts from "../styles/FontInter.module.css";
import mainTheme from "../components/mainTheme";
import styles from "../styles/Home.module.css";
import ExitModal from "../components/ExitModal";
import FormMini from "../components/forms/FormMini";

export default function TemplatePage({ utm }) {
  useEffect(() => {
    dataLayer.push({ event: "optimize.activate" });
  }, []);

  return (
    <ThemeProvider theme={mainTheme}>
      <Head>
        <title>Morris Marketing Group | Solutions - Elm Street Technology</title>
        <meta
          name="description"
          content="Morris Marketing Group - Stay top-of-mind with clients and generate consistent referrals through expert-crafted direct mail and marketing solutions."
        />
        <link rel="icon" href="/favicon.ico" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@weareelmstreet" />
        <meta name="twitter:creator" content="@weareelmstreet" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://elmstreet.com/morris-marketing-group" />
        <meta property="og:title" content="Morris Marketing Group | Solutions - Elm Street Technology" />
        <meta
          property="og:description"
          content="Morris Marketing Group - Stay top-of-mind with clients and generate consistent referrals through expert-crafted direct mail and marketing solutions."
        />
        <meta property="og:image" content="https://elmstreet.com/og_images/elmstreetog.jpg" />
      </Head>
      <CssBaseline className={fonts} />
      <MainHeader />

      <div
        style={{
          backgroundImage: "url('morris-header.png')",
          backgroundPosition: "center",
          backgroundSize: "cover",
          display: "block",
        }}
      >
        <Container maxWidth="lg">
          <Grid container>
            <Grid item md={5} sm={12}>
              <div
                style={{
                  color: "#FFFFFF",
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "row",
                  height: "650px",
                }}
              >
                <div>
                  <Image
                    src="/morris-logo-light.svg"
                    alt={`Morris Marketing Group`}
                    height="100px"
                    width="400px"
                    unoptimized={true}
                  />
                  <Typography variant="h2" component="h1">
                    <span style={{ color: mainTheme.palette.secondary.main }}>
                      Stay Connected, Stay Top-of-Mind: Direct Mail Done Right
                    </span>{" "}
                    <br />
                  </Typography>
                  <p>
                    Stay top-of-mind with clients and generate consistent referrals through expertly crafted direct mail
                    and marketing solutions.
                  </p>
                  <Button variant="contained" color="secondary" href="#form">
                    schedule a demo
                  </Button>
                </div>
              </div>
            </Grid>
          </Grid>
        </Container>
      </div>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "45px",
            paddingBottom: "35px",
          }}
        >
          <div style={{ marginTop: "55px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/build-relationships-morris.png"
                    width="1080px"
                    height="1066px"
                    alt="Morris Marketing Group - Build Relationships That Last"
                  />
                </div>
              </Grid>
              <Grid item md={6} sm={12} style={{ paddingBottom: "20px" }}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>Build Relationships That Last</span>
                </Typography>
                <br />
                <span>
                  At Morris Marketing Group, we understand the importance of staying connected with your clients long
                  after the deal is done. Our direct mail newsletters provide valuable, engaging content that keeps you
                  front and center in your clients’ minds. Strengthen relationships with a personal touch that digital
                  marketing can’t match.
                  <br />
                  <br />
                  <Button variant="contained" color="secondary" href="#form">
                    Learn More
                  </Button>
                </span>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingBottom: "75px",
          }}
        >
          <div style={{ marginTop: "25px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>Deliver Value with Every Interaction</span>
                  <br />
                </Typography>

                <p>
                  Our expertly written newsletters offer a blend of market insights, lifestyle tips, and personalized
                  touches that resonate with your audience. Whether it’s sharing homeownership advice or neighborhood
                  trends, each newsletter is designed to provide value, reinforcing your position as a trusted advisor.
                </p>
              </Grid>
              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/deliver-value.png"
                    width="1080px"
                    height="1066px"
                    alt="Morris Marketing Group - Deliver Value with Every Interaction"
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingBottom: "75px",
          }}
        >
          <div style={{ marginTop: "55px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/proven-system.png"
                    width="1080px"
                    height="1066px"
                    alt="Morris Marketing Group - A Proven System for Referrals"
                  />
                </div>
              </Grid>
              <Grid item md={6} sm={12} style={{ paddingBottom: "20px" }}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>A Proven System for Referrals</span>
                  <br />
                </Typography>

                <p>
                  Referrals are the lifeblood of your business, and Morris Marketing Group makes it easy to generate
                  them. Our Client Referrals System helps you stay in touch with past clients consistently, creating
                  opportunities for them to recommend your services to family and friends.
                </p>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingBottom: "75px",
          }}
        >
          <div style={{ marginTop: "25px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>Marketing That Works for You</span>
                  <br />
                </Typography>

                <p>
                  We know your time is valuable. That’s why our services are designed to be hassle-free and effective.
                  From content creation to mailing logistics, we handle it all so you can focus on what you do best:
                  growing your business and serving your clients.
                </p>
              </Grid>
              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/marketing-that-works.png"
                    width="1080px"
                    height="1066px"
                    alt="Morris Marketing Group - Marketing That Works for You"
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div
        style={{
          backgroundImage: "url('orangebg.webp')",
          backgroundPosition: "center",
          backgroundSize: "cover",
          display: "block",
        }}
      >
        <Container maxWidth="lg" style={{ paddingTop: "80px", paddingBottom: "85px" }}>
          <div style={{}}>
            <a id="form" />
            <Typography variant="h4" component="h4" style={{ color: "#1d1746", textAlign: "center" }}>
              schedule a demo today
            </Typography>
            <div style={{ paddingBottom: "40px", textAlign: "center" }}>
              <strong style={{ color: "#1d1746" }}>
                Connect with Elm Street's Brokerage Success Team today to explore your path to success.
              </strong>
            </div>
            <div style={{ padding: "15px", backgroundColor: "#ffffff" }}>
              <Container
                maxWidth="lg"
                style={{
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  marginTop: "65px",
                  marginBottom: "65px",
                }}
              >
                <Grid container>
                  <Grid item md={2} sm={12} xs={12} />
                  <Grid item md={8} sm={12} xs={12}>
                    <DemoForm
                      buttonText="Schedule Demo"
                      successMsg={
                        <div style={{ color: "#d37800" }}>
                          <strong>
                            We're Excited to Connect with You! A member of the Elm Street Technology team will be in
                            touch with you shortly.
                          </strong>
                          <br />
                          <br />
                        </div>
                      }
                      listId="147986"
                      formName="es2_brokersTeams"
                      fieldList={["first_name", "last_name", "email", "phone", "roll", "solutions", "currentClient"]}
                    />
                  </Grid>
                </Grid>
              </Container>
            </div>
          </div>
        </Container>
      </div>

      <MainFooter />
    </ThemeProvider>
  );
}

export const getServerSideProps = ({ req, res }) => {
  let cookies = getCookie("elmlead", { req, res });
  if (cookies) {
    return { props: { utm: cookies } };
  }
  return { props: {} };
};
